import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { format } from 'date-fns'
import { METRICS } from './RuleEditorPrimitives'
import type { MetricWithIcon, Op } from './RuleEditorPrimitives'
import type { PromotionRuleEditorPanelProps } from './PromotionRuleEditorPanel'
import { ConditionsPanel, SourcesPanel, RoutingPanel, SaveBar } from './RuleEditorPanels'
import type { BidMode, ConditionRow, SourceVM, DestVM } from './RuleEditorPanels'
import {
    MatchBadge,
    eligibleTargetKeys,
    eligibleNegationKeys,
} from './prototypes/sharedRoutingHelpers'
import type { BadgeType, MatchKey, DotStatus } from './prototypes/sharedRoutingHelpers'
import { MatrixModal, BulkEditIcon } from './prototypes/MatrixModal'
import type {
    Source,
    Destination,
    DestinationMatchType,
    AvailableAdGroup,
} from './prototypes/mockData'
import './prototypes/routing-cards.css'
import { FloatingToolbar } from './FloatingToolbar'
import { SmartCampaignBackButton } from './SmartCampaignBackButton'
import PromotionRuleI = Models.PromotionRuleI
import PromotionRuleItemsI = Models.PromotionRuleItemsI

// ── Props ─────────────────────────────────────────────────────────────────────

export interface PromotionRuleEditorNewProps extends PromotionRuleEditorPanelProps {
    setRuleValues: (updates: Partial<PromotionRuleI>) => void
    onSetRuleItem: (
        adGroupId: number,
        campaignId: number,
        updates: Partial<Record<keyof PromotionRuleItemsI, boolean>>,
    ) => void
    conflict?: boolean
    onSave?: () => void
    onCancel?: () => void
    isSaving?: boolean
    isNew?: boolean
    dirty?: boolean
    /** Callback to drop back to the Classic editor */
    onSwitchToClassic?: () => void
}

// ── Metric → rule field mapping ───────────────────────────────────────────────

type RuleFieldPair = { valueKey: keyof PromotionRuleI; opKey: keyof PromotionRuleI }

const METRIC_RULE_KEYS = {
    orders: { valueKey: 'orders_value', opKey: 'orders_operator' },
    spend: { valueKey: 'spend_value', opKey: 'spend_operator' },
    acos: { valueKey: 'acos_value', opKey: 'acos_operator' },
    clicks: { valueKey: 'clicks_value', opKey: 'clicks_operator' },
    blended_profit: { valueKey: 'blended_profit_value', opKey: 'blended_profit_operator' },
    adjusted_orders: { valueKey: 'adjusted_orders_value', opKey: 'adjusted_orders_operator' },
    blended_acos: { valueKey: 'blended_acos_value', opKey: 'blended_acos_operator' },
    adjusted_pages_read: {
        valueKey: 'adjusted_pages_read_value',
        opKey: 'adjusted_pages_read_operator',
    },
    adjusted_estimated_royalties: {
        valueKey: 'adjusted_estimated_royalties_value',
        opKey: 'adjusted_estimated_royalties_operator',
    },
} as const

const OP_TO_RULE: Record<Op, string> = {
    '≥': '>=',
    '≤': '<=',
    '=': '=',
    '>': '>',
    '<': '<',
}
const RULE_TO_OP: Record<string, Op> = {
    '>=': '≥',
    '<=': '≤',
    '=': '=',
    '>': '>',
    '<': '<',
}
const DEFAULT_RULE_OPERATOR_BY_METRIC: Record<string, string> = Object.fromEntries(
    METRICS.map((m) => [m.key, OP_TO_RULE[m.defaultOp]]),
)
const DEFAULT_CONDITION_VALUE = '0'

// ── Init helpers ──────────────────────────────────────────────────────────────

function initConditions(rule: PromotionRuleI, available: MetricWithIcon[]): ConditionRow[] {
    let id = 1
    return available
        .filter((m) => {
            const v = rule[(METRIC_RULE_KEYS as Record<string, RuleFieldPair>)[m.key]?.valueKey]
            return v !== null && v !== undefined && String(v) !== ''
        })
        .map((m) => {
            const keys = (METRIC_RULE_KEYS as Record<string, RuleFieldPair>)[m.key]
            const rawOp = String(rule[keys.opKey] ?? '>=')
            return {
                id: id++,
                metric: m.key,
                op: RULE_TO_OP[rawOp] ?? '≥',
                value: String(rule[keys.valueKey]),
            }
        })
}

function sameConditionRows(a: ConditionRow[], b: ConditionRow[]): boolean {
    if (a.length !== b.length) return false
    return a.every((row, index) => {
        const other = b[index]
        return (
            other && row.metric === other.metric && row.op === other.op && row.value === other.value
        )
    })
}

function deriveBidMode(rule: PromotionRuleI): BidMode {
    if (rule.bid_increase_amount) return 'amount'
    if (rule.bid_increase_percent) return 'percent'
    if (rule.bid_set) return 'set'
    return 'none'
}

// ── Production-only type helpers ──────────────────────────────────────────────

function numericTypeToMatchStr(type: number): string {
    if (type === 3) return 'auto'
    if (type === 2) return 'product'
    return 'keyword'
}

function numericTypeToAdGroupType(type: number): import('./prototypes/mockData').AdGroupType {
    if (type === 3) return 'AUTO'
    if (type === 2) return 'PRODUCT'
    return 'KEYWORD'
}

function numericTypeToBadge(type: number): BadgeType {
    if (type === 3) return 'auto'
    if (type === 2) return 'product'
    return 'broad'
}

function stateToStatus(state: number): DotStatus {
    if (state === 2) return 'paused'
    if (state === 3) return 'archived'
    return 'enabled'
}

// ── Rule item flag maps ───────────────────────────────────────────────────────

const promoteKeyMap: Record<MatchKey, keyof PromotionRuleItemsI> = {
    broad: 'promotion_broad',
    phrase: 'promotion_phrase',
    exact: 'promotion_exact',
    product: 'promotion_products',
}

const negateKeyMap: Partial<Record<MatchKey, keyof PromotionRuleItemsI>> = {
    phrase: 'negation_phrase',
    exact: 'negation_exact',
    product: 'negation_products',
}

function hasAnyPromotion(item: PromotionRuleItemsI): boolean {
    return (
        item.promotion_broad ||
        item.promotion_phrase ||
        item.promotion_exact ||
        item.promotion_products
    )
}

function hasAnyNegation(item: PromotionRuleItemsI): boolean {
    return item.negation_phrase || item.negation_exact || item.negation_products
}

function firstPromoteUpdate(
    item: PromotionRuleItemsI,
): Partial<Record<keyof PromotionRuleItemsI, boolean>> {
    return item.ad_group_type === 2 ? { promotion_products: true } : { promotion_broad: true }
}

function firstNegateUpdate(
    item: PromotionRuleItemsI,
): Partial<Record<keyof PromotionRuleItemsI, boolean>> {
    return item.ad_group_type === 2 ? { negation_products: true } : { negation_phrase: true }
}

// ── VM adapters ───────────────────────────────────────────────────────────────

function toSourceVM(item: PromotionRuleItemsI, onRemove: () => void): SourceVM {
    return {
        key: `${item.campaign_id}-${item.ad_group_id}`,
        adGroupName: item.ad_group_name,
        campaignName: item.campaign_name,
        status: stateToStatus(item.ad_group_state),
        badge: numericTypeToBadge(item.ad_group_type),
        onRemove,
    }
}

function toDestVM(
    item: PromotionRuleItemsI,
    lane: 'promote' | 'negate',
    onToggleMatch: (k: MatchKey) => void,
    onRemove: () => void,
): DestVM {
    return {
        key: `${item.campaign_id}-${item.ad_group_id}`,
        adGroupName: item.ad_group_name,
        campaignName: item.campaign_name,
        status: stateToStatus(item.ad_group_state),
        badge: numericTypeToBadge(item.ad_group_type),
        matchTypeStr: numericTypeToMatchStr(item.ad_group_type),
        isOn: (k) => {
            if (lane === 'promote') return !!(item[promoteKeyMap[k]] as boolean)
            const fk = negateKeyMap[k]
            return !!(fk && (item[fk] as boolean))
        },
        onToggleMatch,
        onRemove,
    }
}

// ── RuleItemPicker (production-specific modal) ────────────────────────────────

const XIcon = () => (
    <svg viewBox="0 0 10 10" width="9" height="9" aria-hidden="true">
        <path
            d="M2 2 L8 8 M8 2 L2 8"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
        />
    </svg>
)

interface PickerProps {
    title: string
    options: PromotionRuleItemsI[]
    onPick: (item: PromotionRuleItemsI) => void
    onClose: () => void
}

function RuleItemPicker({ title, options, onPick, onClose }: PickerProps) {
    const [query, setQuery] = useState('')

    const filtered = options.filter(
        (o) =>
            query === '' ||
            o.ad_group_name.toLowerCase().includes(query.toLowerCase()) ||
            o.campaign_name.toLowerCase().includes(query.toLowerCase()),
    )

    const grouped = filtered.reduce<Record<string, PromotionRuleItemsI[]>>((acc, item) => {
        if (!acc[item.campaign_name]) acc[item.campaign_name] = []
        acc[item.campaign_name].push(item)
        return acc
    }, {})

    return (
        <div className="routing-cards-root">
            <div
                className="pm-picker-scrim"
                onClick={(e) => {
                    if (e.target === e.currentTarget) onClose()
                }}
            >
                <div className="pm-picker">
                    <div className="pm-picker-head">
                        <span className="pm-picker-title">{title}</span>
                        <button
                            type="button"
                            className="icon-btn"
                            onClick={onClose}
                            aria-label="Close"
                        >
                            <XIcon />
                        </button>
                    </div>
                    <div className="pm-picker-search">
                        <input
                            autoFocus
                            type="text"
                            placeholder="Search ad groups…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                    </div>
                    <div className="pm-picker-list">
                        {Object.entries(grouped).length === 0 ? (
                            <div className="pm-picker-empty">No ad groups available</div>
                        ) : (
                            Object.entries(grouped).map(([campaignName, items]) => (
                                <div key={campaignName} className="pm-picker-group">
                                    <div className="pm-picker-camp">{campaignName}</div>
                                    {items.map((item) => (
                                        <button
                                            key={`${item.campaign_id}-${item.ad_group_id}`}
                                            type="button"
                                            className="pm-picker-row"
                                            onClick={() => {
                                                onPick(item)
                                                onClose()
                                            }}
                                        >
                                            <span
                                                className={`status-dot status-dot--${stateToStatus(item.ad_group_state)}`}
                                            />
                                            <span className="pm-picker-name">
                                                {item.ad_group_name}
                                            </span>
                                            <MatchBadge
                                                type={numericTypeToBadge(item.ad_group_type)}
                                            />
                                        </button>
                                    ))}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

// ── Main component ────────────────────────────────────────────────────────────

export const PromotionRuleEditorNew: React.FC<PromotionRuleEditorNewProps> = ({
    rule,
    setRuleValue,
    setRuleValues,
    currencySymbol,
    isKdp = false,
    recommendMaxLookbackDays,
    startDate,
    onSetRuleItem,
    conflict = false,
    onSave,
    onCancel,
    isSaving = false,
    isNew = false,
    dirty = false,
    onSwitchToClassic,
}) => {
    const availableMetrics = useMemo(() => METRICS.filter((m) => isKdp || !m.kdpOnly), [isKdp])
    const ruleSyncKey = `${rule.id ?? 0}:${rule.uuid ?? ''}`

    // ── Keyboard shortcuts ────────────────────────────────────────────────────
    // Cmd/Ctrl+S is owned by PromotionRule.tsx, where the canonical dirty/save
    // state lives. This editor only owns Escape because it maps to local cancel.
    const onCancelRef = useRef(onCancel)
    useEffect(() => {
        onCancelRef.current = onCancel
    })
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !isSaving) onCancelRef.current?.()
        }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [isSaving])

    // ── Conditions state ──────────────────────────────────────────────────────

    const [conditions, setConditions] = useState<ConditionRow[]>(() =>
        initConditions(rule, availableMetrics),
    )
    const [nextId, setNextId] = useState(() => initConditions(rule, availableMetrics).length + 1)
    const [lookback, setLookback] = useState(String(rule.lookback_days ?? ''))

    // ── Bid state ─────────────────────────────────────────────────────────────

    const [bidMode, setBidMode] = useState<BidMode>(() => deriveBidMode(rule))
    const [bidValue, setBidValue] = useState(() =>
        String(rule.bid_increase_amount ?? rule.bid_increase_percent ?? rule.bid_set ?? ''),
    )
    const effectiveDirty =
        dirty || !sameConditionRows(conditions, initConditions(rule, availableMetrics))

    useEffect(() => {
        const nextConditions = initConditions(rule, availableMetrics)
        setConditions(nextConditions)
        setNextId(nextConditions.length + 1)
        setLookback(String(rule.lookback_days ?? ''))
        setBidMode(deriveBidMode(rule))
        setBidValue(
            String(rule.bid_increase_amount ?? rule.bid_increase_percent ?? rule.bid_set ?? ''),
        )
    }, [ruleSyncKey, availableMetrics])

    // ── Picker / bulk-edit state ──────────────────────────────────────────────

    type PickerKind = 'source' | 'promote' | 'negate'
    const [pickerKind, setPickerKind] = useState<PickerKind | null>(null)
    const [bulkOpen, setBulkOpen] = useState(false)

    // ── Lane membership (independent of match-type selection) ─────────────────
    // Track which items are "in" the promote/negate lane separately from which
    // match types are active. This prevents a row from vanishing when the last
    // pill is deselected — it stays visible until the user explicitly removes it.

    const itemKey = (i: PromotionRuleItemsI) => `${i.campaign_id}-${i.ad_group_id}`

    const [promoteIds, setPromoteIds] = useState<Set<string>>(
        () =>
            new Set(
                rule.rule_items
                    .filter((i) => hasAnyPromotion(i) && i.ad_group_type !== 3)
                    .map(itemKey),
            ),
    )
    const [negateIds, setNegateIds] = useState<Set<string>>(
        () => new Set(rule.rule_items.filter(hasAnyNegation).map(itemKey)),
    )

    useEffect(() => {
        setPromoteIds(
            new Set(
                rule.rule_items
                    .filter((i) => hasAnyPromotion(i) && i.ad_group_type !== 3)
                    .map(itemKey),
            ),
        )
        setNegateIds(new Set(rule.rule_items.filter(hasAnyNegation).map(itemKey)))
    }, [ruleSyncKey])

    // ── Derived lists ─────────────────────────────────────────────────────────

    const sources = rule.rule_items.filter((i) => i.use_search_terms)
    const promoted = rule.rule_items.filter(
        (i) => promoteIds.has(itemKey(i)) && i.ad_group_type !== 3,
    )
    const negated = rule.rule_items.filter((i) => negateIds.has(itemKey(i)))

    const userStartDateLabel = format(startDate, 'MMM d, yyyy')
    const bidPrefix = bidMode === 'percent' ? '' : currencySymbol
    const bidSuffix = bidMode === 'percent' ? '%' : ''

    // ── Picker option lists ───────────────────────────────────────────────────

    const sourcePickerOptions = rule.rule_items.filter((i) => !i.use_search_terms)
    const promotePickerOptions = rule.rule_items.filter(
        (i) => !promoteIds.has(itemKey(i)) && i.ad_group_type !== 3,
    )
    const negatePickerOptions = rule.rule_items.filter((i) => !negateIds.has(itemKey(i)))

    // ── Matrix modal data ─────────────────────────────────────────────────────

    const matrixPool: AvailableAdGroup[] = rule.rule_items.map((i) => ({
        id: `${i.campaign_id}-${i.ad_group_id}`,
        campaignName: i.campaign_name,
        adGroupName: i.ad_group_name,
        type: numericTypeToAdGroupType(i.ad_group_type),
        status: stateToStatus(i.ad_group_state),
    }))

    const matrixSources: Source[] = rule.rule_items
        .filter((i) => i.use_search_terms)
        .map((i) => ({
            id: `${i.campaign_id}-${i.ad_group_id}`,
            campaignName: i.campaign_name,
            adGroupName: i.ad_group_name,
            type: numericTypeToAdGroupType(i.ad_group_type),
        }))

    const matrixPromote: Destination[] = rule.rule_items
        .filter((i) => hasAnyPromotion(i) && i.ad_group_type !== 3)
        .map((i) => {
            const matchTypes: DestinationMatchType[] = []
            if (i.promotion_broad) matchTypes.push('broad')
            if (i.promotion_phrase) matchTypes.push('phrase')
            if (i.promotion_exact) matchTypes.push('exact')
            if (i.promotion_products) matchTypes.push('product')
            return {
                id: `${i.campaign_id}-${i.ad_group_id}`,
                campaignName: i.campaign_name,
                adGroupName: i.ad_group_name,
                type: numericTypeToAdGroupType(i.ad_group_type),
                matchTypes,
            }
        })

    const matrixNegate: Destination[] = rule.rule_items
        .filter((i) => hasAnyNegation(i))
        .map((i) => {
            const matchTypes: DestinationMatchType[] = []
            if (i.negation_phrase) matchTypes.push('phrase')
            if (i.negation_exact) matchTypes.push('exact')
            if (i.negation_products) matchTypes.push('product')
            return {
                id: `${i.campaign_id}-${i.ad_group_id}`,
                campaignName: i.campaign_name,
                adGroupName: i.ad_group_name,
                type: numericTypeToAdGroupType(i.ad_group_type),
                matchTypes,
            }
        })

    const handleMatrixSave = ({
        sources,
        promote,
        negate,
    }: {
        sources: Source[]
        promote: Destination[]
        negate: Destination[]
    }) => {
        const srcSet = new Set(sources.map((s) => s.id))
        const promoteMap = new Map(promote.map((d) => [d.id, d.matchTypes]))
        const negateMap = new Map(negate.map((d) => [d.id, d.matchTypes]))
        rule.rule_items.forEach((item) => {
            const key = `${item.campaign_id}-${item.ad_group_id}`
            const proMt = promoteMap.get(key) ?? []
            const negMt = negateMap.get(key) ?? []
            onSetRuleItem(item.ad_group_id, item.campaign_id, {
                use_search_terms: srcSet.has(key),
                promotion_broad: proMt.includes('broad'),
                promotion_phrase: proMt.includes('phrase'),
                promotion_exact: proMt.includes('exact'),
                promotion_products: proMt.includes('product'),
                negation_phrase: negMt.includes('phrase'),
                negation_exact: negMt.includes('exact'),
                negation_products: negMt.includes('product'),
            })
        })
        // Sync lane-membership sets with whatever the matrix committed
        setPromoteIds(new Set(promote.map((d) => d.id)))
        setNegateIds(new Set(negate.map((d) => d.id)))
    }

    // ── Condition handlers (sync local state + rule) ──────────────────────────

    const handleUpdateOp = (id: number, op: Op) => {
        const cond = conditions.find((c) => c.id === id)
        if (cond) {
            setRuleValues({
                [(METRIC_RULE_KEYS as Record<string, RuleFieldPair>)[cond.metric].opKey]:
                    OP_TO_RULE[op],
            })
        }
        setConditions((cs) => cs.map((c) => (c.id === id ? { ...c, op } : c)))
    }

    const handleUpdateValue = (id: number, value: string) => {
        const cond = conditions.find((c) => c.id === id)
        if (cond) {
            const keys = (METRIC_RULE_KEYS as Record<string, RuleFieldPair>)[cond.metric]
            setRuleValues({
                [keys.opKey]: OP_TO_RULE[cond.op],
                [keys.valueKey]: value,
            })
        }
        setConditions((cs) => cs.map((c) => (c.id === id ? { ...c, value } : c)))
    }

    const handleSwapMetric = (id: number, picked: MetricWithIcon) => {
        const cond = conditions.find((c) => c.id === id)
        if (cond) {
            const previousKeys = (METRIC_RULE_KEYS as Record<string, RuleFieldPair>)[cond.metric]
            const nextKeys = (METRIC_RULE_KEYS as Record<string, RuleFieldPair>)[picked.key]
            setRuleValues({
                [previousKeys.valueKey]: '',
                [previousKeys.opKey]: DEFAULT_RULE_OPERATOR_BY_METRIC[cond.metric],
                [nextKeys.opKey]: OP_TO_RULE[picked.defaultOp],
                [nextKeys.valueKey]: DEFAULT_CONDITION_VALUE,
            })
        }
        setConditions((cs) =>
            cs.map((c) =>
                c.id === id
                    ? {
                          ...c,
                          metric: picked.key,
                          op: picked.defaultOp,
                          value: DEFAULT_CONDITION_VALUE,
                      }
                    : c,
            ),
        )
    }

    const handleRemoveCondition = (id: number) => {
        const cond = conditions.find((c) => c.id === id)
        if (cond) {
            const keys = (METRIC_RULE_KEYS as Record<string, RuleFieldPair>)[cond.metric]
            setRuleValues({
                [keys.valueKey]: '',
                [keys.opKey]: DEFAULT_RULE_OPERATOR_BY_METRIC[cond.metric],
            })
        }
        setConditions((cs) => cs.filter((c) => c.id !== id))
    }

    const handleAddCondition = () => {
        const usedKeys = new Set(conditions.map((c) => c.metric))
        const next = availableMetrics.find((m) => !usedKeys.has(m.key))
        if (!next) return
        const id = nextId
        setNextId((n) => n + 1)
        const keys = (METRIC_RULE_KEYS as Record<string, RuleFieldPair>)[next.key]
        setRuleValues({
            [keys.opKey]: OP_TO_RULE[next.defaultOp],
            [keys.valueKey]: DEFAULT_CONDITION_VALUE,
        })
        setConditions((cs) => [
            ...cs,
            { id, metric: next.key, op: next.defaultOp, value: DEFAULT_CONDITION_VALUE },
        ])
    }

    const handleLookbackChange = (v: string) => {
        setLookback(v)
        setRuleValue('lookback_days')(v)
    }

    // ── Bid handlers ──────────────────────────────────────────────────────────

    const handleBidModeChange = (mode: BidMode) => {
        setBidMode(mode)
        setRuleValue('bid_set')('')
        setRuleValue('bid_increase_amount')('')
        setRuleValue('bid_increase_percent')('')
        if (mode === 'amount' && bidValue) setRuleValue('bid_increase_amount')(bidValue)
        else if (mode === 'percent' && bidValue) setRuleValue('bid_increase_percent')(bidValue)
        else if (mode === 'set' && bidValue) setRuleValue('bid_set')(bidValue)
        if (mode === 'none') setBidValue('')
    }

    const handleBidValueChange = (v: string) => {
        setBidValue(v)
        if (bidMode === 'amount') setRuleValue('bid_increase_amount')(v)
        else if (bidMode === 'percent') setRuleValue('bid_increase_percent')(v)
        else if (bidMode === 'set') setRuleValue('bid_set')(v)
    }

    // ── Rule item handlers ────────────────────────────────────────────────────

    const handlePickerPick = (item: PromotionRuleItemsI) => {
        const key = itemKey(item)
        if (pickerKind === 'source') {
            onSetRuleItem(item.ad_group_id, item.campaign_id, { use_search_terms: true })
        } else if (pickerKind === 'promote') {
            onSetRuleItem(item.ad_group_id, item.campaign_id, firstPromoteUpdate(item))
            setPromoteIds((prev) => new Set(prev).add(key))
        } else if (pickerKind === 'negate') {
            onSetRuleItem(item.ad_group_id, item.campaign_id, firstNegateUpdate(item))
            setNegateIds((prev) => new Set(prev).add(key))
        }
        setPickerKind(null)
    }

    // ── VM arrays ─────────────────────────────────────────────────────────────

    const sourceVMs: SourceVM[] = sources.map((item) =>
        toSourceVM(item, () =>
            onSetRuleItem(item.ad_group_id, item.campaign_id, { use_search_terms: false }),
        ),
    )

    const promoteVMs: DestVM[] = promoted.map((item) =>
        toDestVM(
            item,
            'promote',
            (k) =>
                onSetRuleItem(item.ad_group_id, item.campaign_id, {
                    [promoteKeyMap[k]]: !(item[promoteKeyMap[k]] as boolean),
                } as Partial<Record<keyof PromotionRuleItemsI, boolean>>),
            () => {
                onSetRuleItem(item.ad_group_id, item.campaign_id, {
                    promotion_broad: false,
                    promotion_phrase: false,
                    promotion_exact: false,
                    promotion_products: false,
                })
                setPromoteIds((prev) => {
                    const next = new Set(prev)
                    next.delete(itemKey(item))
                    return next
                })
            },
        ),
    )

    const negateVMs: DestVM[] = negated.map((item) =>
        toDestVM(
            item,
            'negate',
            (k) => {
                const fk = negateKeyMap[k]
                if (!fk) return
                onSetRuleItem(item.ad_group_id, item.campaign_id, {
                    [fk]: !(item[fk] as boolean),
                } as Partial<Record<keyof PromotionRuleItemsI, boolean>>)
            },
            () => {
                onSetRuleItem(item.ad_group_id, item.campaign_id, {
                    negation_phrase: false,
                    negation_exact: false,
                    negation_products: false,
                })
                setNegateIds((prev) => {
                    const next = new Set(prev)
                    next.delete(itemKey(item))
                    return next
                })
            },
        ),
    )

    const pickerTitle =
        pickerKind === 'source'
            ? 'Add source ad group'
            : pickerKind === 'promote'
              ? 'Add promote destination'
              : 'Add negate destination'

    const pickerOptions =
        pickerKind === 'source'
            ? sourcePickerOptions
            : pickerKind === 'promote'
              ? promotePickerOptions
              : negatePickerOptions
    const titlePlaceholder = 'Untitled rule'
    const titleTextForWidth = (rule.name || titlePlaceholder).trimEnd() || titlePlaceholder
    const titleMeasureRef = useRef<HTMLSpanElement>(null)
    const [titleWidth, setTitleWidth] = useState<number | undefined>()
    useLayoutEffect(() => {
        const measure = titleMeasureRef.current
        if (!measure) return
        setTitleWidth(Math.ceil(measure.getBoundingClientRect().width) + 12)
    }, [titleTextForWidth])

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className={`max-w-3xl mx-auto${onSave ? ' pb-16' : ''}`}>
            <FloatingToolbar onSwitchToClassic={onSwitchToClassic} />

            {/* ── Rule name title ── */}
            <div className="pt-16 mb-8">
                {onCancel && (
                    <div className="mb-3">
                        <SmartCampaignBackButton onBack={onCancel} />
                    </div>
                )}
                <div className="relative inline-block max-w-full">
                    <span
                        ref={titleMeasureRef}
                        aria-hidden="true"
                        className="pointer-events-none invisible absolute left-0 top-0 whitespace-pre rounded-lg border border-transparent px-1 py-0.5 text-[24px] font-[700] tracking-[-0.03em] leading-none"
                    >
                        {titleTextForWidth}
                    </span>
                    <input
                        value={rule.name ?? ''}
                        onChange={(e) => setRuleValue('name')(e.target.value)}
                        style={{ width: titleWidth, maxWidth: '100%' }}
                        className="block rounded-lg text-[24px] font-[700] tracking-[-0.03em] leading-none bg-transparent outline-none border border-transparent focus:ring-2 focus:ring-[#3391FF]/20 focus:border-[#3391FF] hover:bg-[#F8FAFC] placeholder:text-[#CDD6DF] px-1 py-0.5 -ml-1 text-[#242529] transition-colors truncate"
                        placeholder={titlePlaceholder}
                        aria-label="Rule name"
                    />
                </div>
            </div>

            {conflict && (
                <div
                    className="mb-3 flex gap-2.5 rounded-lg px-3 py-2.5 text-[13px] leading-relaxed"
                    style={{ background: '#FFFBEB', border: '1px solid rgba(217,119,6,.2)' }}
                >
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        className="shrink-0 mt-0.5 text-amber-500"
                    >
                        <path
                            d="M8 2.5L1.5 13.5h13L8 2.5z"
                            stroke="currentColor"
                            strokeWidth="1.4"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M8 6.5v3"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                        />
                        <circle cx="8" cy="11.5" r="0.75" fill="currentColor" />
                    </svg>
                    <div className="text-amber-900">
                        <span className="font-[600]">Ad group conflict.</span> You are trying to
                        promote or negate search terms as both keywords and product targets into a
                        single ad group. Amazon does not allow this — for a single ad group you can
                        have either keywords or product targets, but not both.
                    </div>
                </div>
            )}

            <div className="bg-white overflow-visible divide-y divide-[#F0F2F5]">
                <ConditionsPanel
                    isKdp={isKdp}
                    conditions={conditions}
                    lookback={lookback}
                    currencySymbol={currencySymbol}
                    recommendMaxLookbackDays={recommendMaxLookbackDays}
                    userStartDateLabel={userStartDateLabel}
                    onAddCondition={handleAddCondition}
                    onRemoveCondition={handleRemoveCondition}
                    onSwapMetric={handleSwapMetric}
                    onUpdateOp={handleUpdateOp}
                    onUpdateValue={handleUpdateValue}
                    onLookbackChange={handleLookbackChange}
                />
                <SourcesPanel
                    sources={sourceVMs}
                    onAdd={() => setPickerKind('source')}
                    action={
                        <div className="routing-cards-root">
                            <button
                                type="button"
                                className="pm-matrix-open-btn"
                                onClick={() => setBulkOpen(true)}
                            >
                                <BulkEditIcon /> Bulk edit
                            </button>
                        </div>
                    }
                />
                <RoutingPanel
                    promote={promoteVMs}
                    negate={negateVMs}
                    onAddPromote={() => setPickerKind('promote')}
                    onAddNegate={() => setPickerKind('negate')}
                    bidMode={bidMode}
                    bidValue={bidValue}
                    bidPrefix={bidPrefix}
                    bidSuffix={bidSuffix}
                    onBidModeChange={handleBidModeChange}
                    onBidValueChange={handleBidValueChange}
                    currencySymbol={currencySymbol}
                    action={
                        <div className="routing-cards-root">
                            <button
                                type="button"
                                className="pm-matrix-open-btn"
                                onClick={() => setBulkOpen(true)}
                            >
                                <BulkEditIcon /> Bulk edit
                            </button>
                        </div>
                    }
                />
            </div>

            {pickerKind && (
                <RuleItemPicker
                    title={pickerTitle}
                    options={pickerOptions}
                    onPick={handlePickerPick}
                    onClose={() => setPickerKind(null)}
                />
            )}

            {bulkOpen && (
                <MatrixModal
                    sources={matrixSources}
                    promote={matrixPromote}
                    negate={matrixNegate}
                    availableAdGroups={matrixPool}
                    onSave={handleMatrixSave}
                    onClose={() => setBulkOpen(false)}
                />
            )}

            {onSave && (effectiveDirty || isSaving) && (
                <SaveBar
                    onSave={onSave}
                    onCancel={onCancel ?? (() => {})}
                    isSaving={isSaving}
                    isNew={isNew}
                    isValid={!conflict}
                />
            )}
        </div>
    )
}
